import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { CSSProperties, FunctionComponent, HTMLAttributes, KeyboardEvent, ReactNode, Ref } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { UseFormRegisterReturn } from 'react-hook-form';

import { Input } from '../../../core/input/Input';
import { useComposedRefs } from '../../../utils/compose-refs';

const TITLE_TYPOGRAPHY = 'text-left text-black-900 text-[1.375rem] font-medium leading-normal';

const TITLE_STYLE: CSSProperties = { overflowWrap: 'anywhere' };

type TitleProps = HTMLAttributes<HTMLDivElement> & {
    readonly children: ReactNode;
};

export const Title: FunctionComponent<TitleProps> = ({ children, className, style, ...rest }) => (
    <div className={clsx(TITLE_TYPOGRAPHY, className)} style={{ ...TITLE_STYLE, ...style }} {...rest}>
        {children}
    </div>
);

type SidepanelTitleProps = {
    readonly className?: string;
    readonly commentHighlightClassName?: string;
    readonly hasComment?: boolean;
    readonly inputRef?: Ref<HTMLInputElement>;
    readonly isCommentActive?: boolean;
    readonly isEditing: boolean;
    readonly placeholder?: string;
    readonly title: string;
    readonly onInput?: () => void;
    readonly onRowClick?: () => void;
    readonly register?: UseFormRegisterReturn;
    readonly rowAttrs?: Record<string, string>;
    readonly stamps?: ReactNode;
};

const TITLE_INPUT_TYPOGRAPHY =
    'text-left !text-black-900 !text-[1.375rem] !font-medium !leading-normal !tracking-normal';

const INPUT_OVERRIDE_CLASSES = clsx(
    'relative box-border',
    '!p-0 !border-0 !bg-transparent !rounded-none !transition-none',
    TITLE_INPUT_TYPOGRAPHY,
);

export const SidepanelTitle: FunctionComponent<SidepanelTitleProps> = ({
    className,
    commentHighlightClassName,
    hasComment = false,
    inputRef,
    isCommentActive = false,
    isEditing,
    onInput,
    placeholder,
    onRowClick,
    register,
    rowAttrs,
    stamps,
    title,
}) => {
    const [inputElement, setInputElement] = useState<HTMLInputElement | null>(null);
    const mirrorRef = useRef<HTMLSpanElement | null>(null);
    const composedInputRef = useComposedRefs<HTMLInputElement>(register?.ref, inputRef, setInputElement);

    const syncHighlightOverlay = useCallback((): void => {
        const mirror = mirrorRef.current;
        if (is.null(inputElement) || is.null(mirror)) return;
        if (mirror.textContent === inputElement.value) return;
        mirror.textContent = inputElement.value;
    }, [inputElement]);

    useLayoutEffect(() => {
        syncHighlightOverlay();
    });

    useEffect(() => {
        if (is.null(inputElement)) return;
        inputElement.addEventListener('input', syncHighlightOverlay);
        inputElement.addEventListener('change', syncHighlightOverlay);
        return () => {
            inputElement.removeEventListener('input', syncHighlightOverlay);
            inputElement.removeEventListener('change', syncHighlightOverlay);
        };
    }, [inputElement, syncHighlightOverlay]);

    const rowClasses = clsx('flex w-full min-w-0 flex-wrap items-center justify-between gap-m', className);
    const hasHighlight = !is.undefined(commentHighlightClassName) && commentHighlightClassName.trim().length > 0;
    const titleLayoutClasses = 'min-w-[12rem] flex-1';
    const titleHighlightClasses = clsx(
        'transition-colors duration-200 ease',
        hasHighlight && 'rounded-sm px-xs',
        commentHighlightClassName,
    );

    const { ref: _registerRef, ...registerProps } = register ?? {};

    const content = isEditing ? (
        <div
            className={titleLayoutClasses}
            data-comment-active={isCommentActive}
            data-has-comment={hasComment}
            data-testid="sidepanel-title-input-wrapper"
        >
            <div className="relative overflow-hidden">
                <span
                    aria-hidden={true}
                    className={clsx(
                        'pointer-events-none absolute top-0 left-0 max-w-full whitespace-pre',
                        'text-left text-[1.375rem] font-medium leading-normal tracking-normal text-transparent',
                        titleHighlightClasses,
                        hasHighlight && '-ml-xs',
                    )}
                    data-testid="sidepanel-title-text"
                    ref={mirrorRef}
                />
                <Input
                    {...registerProps}
                    ref={composedInputRef}
                    className={INPUT_OVERRIDE_CLASSES}
                    placeholder={placeholder}
                    onInput={onInput}
                />
            </div>
        </div>
    ) : (
        <Title
            className="min-w-48 flex-1"
            data-comment-active={isCommentActive}
            data-has-comment={hasComment}
            data-testid="sidepanel-title"
        >
            <span className={clsx('inline', titleHighlightClasses)} data-testid="sidepanel-title-text">
                {title}
            </span>
        </Title>
    );

    const isInteractive = !is.undefined(onRowClick) && !isEditing;
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (!isInteractive) return;
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onRowClick?.();
        }
    };
    const interactiveAttrs = isInteractive
        ? { onClick: onRowClick, onKeyDown: handleKeyDown, role: 'button', tabIndex: 0 }
        : {};

    if (is.undefined(stamps)) {
        return (
            <div className={rowClasses} {...interactiveAttrs} {...rowAttrs}>
                {content}
            </div>
        );
    }

    return (
        <div className={rowClasses} {...interactiveAttrs} {...rowAttrs}>
            {content}
            <div
                aria-hidden={isEditing ? true : undefined}
                className={clsx('flex shrink-0 items-center justify-end gap-s', isEditing && 'invisible')}
                data-testid="sidepanel-title-stamps-slot"
            >
                {stamps}
            </div>
        </div>
    );
};
